import { ref } from 'vue';
import {
  COMMANDS,
  MEDIA_TYPE,
  DISPLAY_NAME,
  ALLOW_DUPLICATED_PARTICIPANTS,
  CLINICIAN_STREAMS_TO_ALLOW_ONLY_IN_CONSULTATION,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import { SmartGlassesClient } from '@/Services/SocketioClient';
import LogService from '@/Services/LogService';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import useUsersPresenceStore from '@/Stores/useUsersPresenceStore';

function useGroupCallHandlers({
  remoteUserProps,
  videoAudioRefs,
  onConferenceJoined,
  room,
  vuzixStreamId,
  moderatorId,
  localAudioLevel,
  localConnectionQuality,
  myJitsiId,
  resetMicState,
  smartphoneStreamId,
}) {
  const userIds = ref([]);
  // const callParticipantUserIdsCount = computed(() => itemsInArrayCount(userIds.value));
  const sessionStore = useSessionStore();
  const streamingDeviceStore = useIOTDevicesStore();
  const multiCameraStore = useLocalStreamingDevicesStore();
  const presenceUsersStore = useUsersPresenceStore();

  const updateActiveCallAVState = (userId, trackType, trackState, jitsiUserId) => {
    sessionStore.addActiveCallParticipants({
      [userId]: {
        [trackType]: trackState,
        isRemoteUser: true,
        jitsiUserId,
      },
    });
  };

  const toggleHand = (state, userId, time) => {
    sessionStore.setHandRaised({
      [userId]: {
        state,
        time,
      },
    });
  };

  // Disallow clinician streams.
  const shouldDisallowSomeClinicianStreams = (displayName) =>
    (sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION ||
      sessionStore.currentPhase === VICE_SESSION_PHASES.DEBRIEF) &&
    CLINICIAN_STREAMS_TO_ALLOW_ONLY_IN_CONSULTATION.includes(displayName);

  // Events
  const onRemoteTrack = (track) => {
    track.on(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, (newTrack) => {
      // Update the UI and microphone when the audio or video track changes for a remote user
      const senderId = newTrack.getParticipantId();
      const mediaType = newTrack.getType();
      const isMuted = newTrack.isMuted();

      if (remoteUserProps[senderId]) {
        remoteUserProps[senderId][mediaType] = !isMuted;
      }

      // setting the updated video and audio state for the participants
      const participantUserId = remoteUserProps[senderId].userId;
      updateActiveCallAVState(participantUserId, mediaType, !isMuted, senderId);
    });

    const participant = track.getParticipantId();

    if (remoteUserProps[participant] && videoAudioRefs[participant]) {
      const shareScreenUserTrack = /ScreenshareUser/.test(remoteUserProps[participant]?.userId);
      const mediaType = track.getType();
      const isMuted = track.isMuted();
      remoteUserProps[participant][mediaType] = !isMuted;

      if (track?.getType() === MEDIA_TYPE.VIDEO) {
        const videoEl = videoAudioRefs[participant].find((el) => el.nodeName === 'VIDEO');
        track.attach(videoEl);
      }

      if (track?.getType() === MEDIA_TYPE.AUDIO) {
        const audioEl = videoAudioRefs[participant].find((el) => el.nodeName === 'AUDIO');
        if (sessionStore.isClinician && shareScreenUserTrack) {
          // No need to add shareScreenUser audio track for clinician to avoid echo effect
        } else {
          track.attach(audioEl);
        }
      }

      remoteUserProps[participant].tracks.push(track);

      // Update the visible screenshare flag
      if (shareScreenUserTrack) {
        remoteUserProps[participant].screenshareOff = false;
      }

      // setting the updated video and audio state for the participants
      const participantUserId = remoteUserProps[participant].userId;
      updateActiveCallAVState(participantUserId, mediaType, !isMuted, participant);
    }
  };

  const onLocalTrack = async (track) => {
    await LogService.logLocalTrack(track, 'ADDED');

    track.on(JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, async () => {
      await LogService.logLocalTrack(track, 'STOPPED');
    });

    track.on(JitsiMeetJS.events.track.TRACK_REMOVED, async () => {
      await LogService.logLocalTrack(track, 'REMOVED');
    });
  };

  const onTrackAdded = (track) => {
    if (!track.isLocal()) {
      onRemoteTrack(track);
    } else {
      onLocalTrack(track);
    }
  };

  // remove event listeners on track
  // track.removeEventListener(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, () => {});
  // when a track is completely removed, assume user has left.
  const onTrackRemoved = (track) => {
    const id = track.getParticipantId();

    // For a screenshare, just detach the user
    if (/ScreenshareUser/.test(remoteUserProps[id]?.userId)) {
      track.detach(videoAudioRefs[id][1]);
      remoteUserProps[id].screenshareOff = true;
    } else {
      onUserLeft(id);
    }
  };

  /**
   * Prevent a NavigationUser, Observer or Vuzix device from joining the Group Call.
   * Set the various properties for a new participant who just joins a stream
   *
   * TODO: Disallow a user in the session from joining multiple times
   * TODO: Make sure the right profile picture shows when user id and moderator id are the same
   */
  const onUserJoined = async (id, user) => {
    const hasDuplicateUsersInSession = () => {
      const values = presenceUsersStore.presentUsers.map((obj) => obj.unique_id);
      return new Set(values).size !== values.length;
    };

    const displayName = user.getDisplayName();
    // If we are in the Introduction or Debrief stage
    // Disallow stethoscope and vuzix from being displayed
    if (shouldDisallowSomeClinicianStreams(displayName)) {
      if (displayName === DISPLAY_NAME.VUZIX) {
        sessionStore.deviceMessageSender.toggleSmartglassAudioIO(true);
      }
      return;
    }

    if (displayName !== DISPLAY_NAME.NAVIGATION_USER && displayName !== DISPLAY_NAME.OBSERVER) {
      const lastDashIndex = displayName?.lastIndexOf('-');
      const name = displayName?.substr(0, lastDashIndex);
      const userId = displayName?.substr(lastDashIndex + 1);

      userIds.value = room.value?.getParticipants().map((participant) => {
        const dashIndex = participant.getDisplayName()?.lastIndexOf('-');
        return participant.getDisplayName()?.substr(dashIndex + 1);
      });

      // callParticipantUserIdsCount.value?.[sessionStore.authUser.id]
      // means this currently authenticated user is joining again
      // This means this current remote user is trying to join again
      if (hasDuplicateUsersInSession()) {
        if (ALLOW_DUPLICATED_PARTICIPANTS) {
          console.log('duplicates are allowed, proceed');
        } else {
          // We can have an improved UI to show that a duplicate user is trying to join the
          // The group call
          console.log('duplicates not allowed');
          return;
        }
      }

      if (!remoteUserProps[id]) {
        remoteUserProps[id] = {};
      }
      remoteUserProps[id].name = name;
      remoteUserProps[id].role = user?.getRole();
      remoteUserProps[id].userId = userId;
      remoteUserProps[id].tracks = [];

      remoteUserProps[id][MEDIA_TYPE.AUDIO] = false;
      remoteUserProps[id][MEDIA_TYPE.VIDEO] = false;
      remoteUserProps[id].isOnHold = false;
      remoteUserProps[id].jitsiUserId = id;

      // Add a videoHidden Property to hide video on Clinician side for external cameras.
      remoteUserProps[id].isVideoHidden = false;

      // flag to toggle the screenshare on and off
      // for a screenshare user, the name is Clinician-ScreenshareUser
      // so when we do the userId extraction at the top, userId = ScreenshareUser
      if (/ScreenshareUser/.test(displayName)) {
        remoteUserProps[id].screenshareOff = true;
      }

      // Initiate smartglass socket client after remoteUserProps instance has been added.
      if (displayName === DISPLAY_NAME.VUZIX) {
        vuzixStreamId.value = id;
        sessionStore.vuzixStreamId = id;
        streamingDeviceStore.iotSockets.smartglasses = await SmartGlassesClient(
          sessionStore.sessionId,
        );
        multiCameraStore.changeAudioIOtoExternal(false);
      }

      if (displayName === DISPLAY_NAME.SMARTPHONE) {
        if (smartphoneStreamId) smartphoneStreamId.value = id;
        sessionStore.smartphoneStreamId = id;
      }

      // Add the remoteUserProperties in session store.
      updateActiveCallAVState(userId, MEDIA_TYPE.AUDIO, false, id);
      updateActiveCallAVState(userId, MEDIA_TYPE.VIDEO, false, id);
    }
  };
  /**
   * When a user leaves the room, remove their media
   * tracks and clear them from the remoteUserProps
   */
  const onUserLeft = (id) => {
    if (!remoteUserProps[id]) {
      return;
    }

    // Disconnect Vuzix
    if (remoteUserProps[id].userId === DISPLAY_NAME.VUZIX) {
      vuzixStreamId.value = null;
      sessionStore.vuzixStreamId = null;
      streamingDeviceStore.iotSockets.smartglasses = null;
      multiCameraStore.changeAudioIOtoExternal(false);
    }

    // Disconnect Smartphone
    if (remoteUserProps[id].userId === DISPLAY_NAME.SMARTPHONE) {
      smartphoneStreamId.value = null;
      sessionStore.smartphoneStreamId = null;
      streamingDeviceStore.iotSockets.smartphone = null;
    }

    if (sessionStore.isHandRaised(remoteUserProps[id].userId)) {
      toggleHand(false, remoteUserProps[id].userId, 0);
    }

    if (remoteUserProps[id]?.tracks && videoAudioRefs[id]) {
      for (const track of remoteUserProps[id].tracks) {
        if (track.getType() === MEDIA_TYPE.VIDEO) {
          const videoEl = videoAudioRefs[id].find((el) => el.nodeName === 'VIDEO');
          track.detach(videoEl);
        }
        if (track.getType() === MEDIA_TYPE.AUDIO) {
          const audioEl = videoAudioRefs[id].find((el) => el.nodeName === 'AUDIO');
          track.detach(audioEl);
        }
      }
    }

    if (moderatorId.value === id) moderatorId.value = null;
    delete remoteUserProps[id];
  };

  const onAudioChanged = (id, audioLevel) => {
    if (myJitsiId.value == id) {
      localAudioLevel.value = audioLevel;
    } else if (remoteUserProps[id]) {
      remoteUserProps[id].audioLevel = audioLevel;
    }
  };

  /**
   * Force the clinician to always have moderator role
   * Set the value for moderatorId for the Clinician to always
   * be the first on the Video Grid
   */
  const onParticipantPropertyChanged = (user, propertyKey, oldValue, newValue) => {
    if (user !== undefined && propertyKey === 'avatar') {
      remoteUserProps[user.getId()].avatar = newValue;
    }
    const displayName = user.getDisplayName();

    if (displayName !== DISPLAY_NAME.NAVIGATION_USER && displayName !== DISPLAY_NAME.VUZIX) {
      if (user.getProperty('isClinician') === 'true' && remoteUserProps[user.getId()]) {
        user.setRole('moderator');
        moderatorId.value = user.getId();
        remoteUserProps[moderatorId.value].role = user.getRole();
      }
    }
  };

  const onRemoteStatsUpdated = (id, stats) => {
    if (remoteUserProps[id]) {
      remoteUserProps[id].connectionQuality = stats.connectionQuality;
    }
  };

  const onLocalStatsUpdated = (stats) => {
    localConnectionQuality.value = stats.connectionQuality;
  };

  const events = {
    CONFERENCE: {
      TRACK_ADDED: onTrackAdded,
      TRACK_REMOVED: onTrackRemoved,
      CONFERENCE_JOINED: onConferenceJoined,
      USER_JOINED: onUserJoined,
      USER_LEFT: onUserLeft,
      TRACK_AUDIO_LEVEL_CHANGED: onAudioChanged,
      PARTICIPANT_PROPERTY_CHANGED: onParticipantPropertyChanged,
    },
    CONN_QUALITY: {
      REMOTE_STATS_UPDATED: onRemoteStatsUpdated,
      LOCAL_STATS_UPDATED: onLocalStatsUpdated,
    },
  };

  //   Commands

  /**
   * MuteAllParticipants Listener
   * Triggered by the Clinician. Mutes all participants audio
   */
  const onMuteAllParticipants = () => {
    if (!sessionStore.isClinician) {
      resetMicState(false);
    }
  };

  /**
   * Put On Hold Command listener
   * Toggle the on hold indicator for a specific user
   */
  const onPutOnHold = ({ attributes }) => {
    if (attributes.isOnHold === 'true' && remoteUserProps[attributes.userRoomId]) {
      remoteUserProps[attributes.userRoomId].isOnHold = true;
    }

    if (attributes.isOnHold === 'false' && remoteUserProps[attributes.userRoomId]) {
      remoteUserProps[attributes.userRoomId].isOnHold = false;
    }
  };

  // This command listener was added because TRACK_REMOVED AND TRACK_ADDED is not
  // functioning as expected for the screenshare toggling
  const onEndScreenshare = ({ attributes }) => {
    const screenshareUserId = Object.keys(remoteUserProps).find((jitsiUserId) =>
      /ScreenshareUser/.test(remoteUserProps[jitsiUserId].userId),
    );

    if (screenshareUserId) {
      if (attributes.isEnded === 'true') {
        remoteUserProps[screenshareUserId].tracks[0].detach(videoAudioRefs[screenshareUserId][1]);
        remoteUserProps[screenshareUserId].screenshareOff = true;
      } else {
        remoteUserProps[screenshareUserId].screenshareOff = false;
        if (remoteUserProps[screenshareUserId].tracks[0]) {
          remoteUserProps[screenshareUserId].tracks[0].attach(videoAudioRefs[screenshareUserId][1]);
        }
      }
    }
  };

  const onMuteSingleParticipant = ({ attributes }) => {
    // Find the participant
    if (attributes.participantJitsiId === myJitsiId.value) {
      resetMicState(false);
    }
  };

  const onKickParticipant = ({ attributes }) => {
    if (attributes.participantJitsiId === myJitsiId.value) {
      // Take the user to the dashboard
      window.location.replace(route('dashboard'));
    }
  };

  const onRaiseHand = ({ attributes }) => {
    toggleHand(attributes.state, attributes.userId, attributes.time);
  };

  const commands = {
    [COMMANDS.RAISE_HAND]: onRaiseHand,
    [COMMANDS.PUT_ON_HOLD]: onPutOnHold,
    [COMMANDS.END_SCREENSHARE]: onEndScreenshare,
    [COMMANDS.KICK_PARTICIPANT]: onKickParticipant,
    [COMMANDS.MUTE_ALL_PARTICIPANTS]: onMuteAllParticipants,
    [COMMANDS.MUTE_SINGLE_PARTICIPANT]: onMuteSingleParticipant,
  };

  return {
    events,
    commands,
  };
}

export default useGroupCallHandlers;
